/**
 * Context Menu — right-click menus for project rows, project panel body, and shot launcher
 */
import { projectStore } from '../stores/projectStore';
import { selectionStore } from '../stores/selectionStore';
import { monitorStore } from '../stores/monitorStore';
import { shotStore } from '../stores/shotStore';
import { doTake } from './program';
import {
  newGraphic,
  newShotLayout,
  importMedia,
  importAfterEffects,
  importLayer,
  importGraphic,
  importShotLayout,
  importTransitions,
  addLiveCamera,
  flashStatus,
} from './menuSystem';

interface ContextItem {
  label: string;
  action?: () => void;
  separator?: boolean;
  disabled?: boolean;
  icon?: string;
  children?: ContextItem[];
}

export function initContextMenu(): void {
  // Project table right-click on a row
  const projectBody = document.getElementById('project-tbody');
  projectBody?.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    e.stopPropagation();
    const row = (e.target as HTMLElement).closest('tr[data-item-id]') as HTMLElement;
    if (row) {
      showProjectItemMenu(e, row.dataset.itemId!);
    } else {
      // Clicked empty area of tbody — show add menu
      showProjectPanelMenu(e);
    }
  });

  // Project panel body right-click (outside table rows)
  const projectPanel = document.querySelector('.project-panel .panel-body');
  projectPanel?.addEventListener('contextmenu', (e) => {
    // If the event already hit tbody handler, skip
    if ((e.target as HTMLElement).closest('#project-tbody')) return;
    e.preventDefault();
    showProjectPanelMenu(e);
  });

  // Shot launcher right-click
  const shotBody = document.querySelector('.shot-launcher .panel-body');
  shotBody?.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    const card = (e.target as HTMLElement).closest('.shot-card') as HTMLElement;
    if (card) {
      showShotMenu(e, card.dataset.shotId!);
    }
  });
}

/* ── Project panel body menu (New / Import / Manage Shot Contents / Delete) ── */

function showProjectPanelMenu(e: Event): void {
  const me = e as MouseEvent;

  const selectedIds = selectionStore.getState().selectedProjectItemIds;

  const items: ContextItem[] = [
    {
      label: 'New',
      children: [
        { label: 'Graphic', icon: '/assets/icons/graphic.png', action: () => newGraphic() },
        { label: 'After Effects Comp', icon: '/assets/icons/ae.png', action: () => importAfterEffects() },
        { label: 'Shot Layout', icon: '/assets/icons/shot.png', action: () => newShotLayout() },
        { label: 'Media Sequence', icon: '/assets/icons/sequence.png', action: () => importMedia() },
        { label: 'Transition', icon: '/assets/icons/transition.png', action: () => flashStatus('Transition') },
        {
          label: 'Live Audio/Video',
          children: [
            { label: 'Windows Device', action: () => addLiveCamera() },
            { label: 'Blackmagic Decklink', action: () => addLiveCamera() },
            { label: 'Audio Device', disabled: true },
            { label: 'Screen', disabled: true },
          ],
        },
      ],
    },
    {
      label: 'Import',
      children: [
        { label: 'Layer...', action: () => importLayer() },
        { label: 'Graphic...', action: () => importGraphic() },
        { label: 'Media...', action: () => importMedia() },
        { label: 'After Effects...', action: () => importAfterEffects() },
        { label: 'Shot Layout...', action: () => importShotLayout() },
        { label: 'Transitions...', action: () => importTransitions() },
      ],
    },
    {
      label: 'Manage Shot Contents',
      children: [
        { label: 'Remove from shot', disabled: true, children: [] },
        { label: 'Color Label', children: [
          { label: 'None', action: () => flashStatus('Color Label: None') },
          { label: 'Red', action: () => flashStatus('Color Label: Red') },
          { label: 'Orange', action: () => flashStatus('Color Label: Orange') },
          { label: 'Yellow', action: () => flashStatus('Color Label: Yellow') },
          { label: 'Green', action: () => flashStatus('Color Label: Green') },
          { label: 'Blue', action: () => flashStatus('Color Label: Blue') },
          { label: 'Purple', action: () => flashStatus('Color Label: Purple') },
        ]},
        { label: 'Export shot layout...', action: () => flashStatus('Export shot layout...') },
      ],
    },
    { label: '', separator: true },
    {
      label: selectedIds.length > 1 ? `Delete ${selectedIds.length} items` : 'Delete',
      disabled: selectedIds.length === 0,
      action: () => {
        const ids = selectionStore.getState().selectedProjectItemIds;
        if (ids.length > 0) {
          projectStore.getState().removeItems(ids);
          selectionStore.getState().clearSelection();
        }
      },
    },
  ];

  showContextMenu(me.clientX, me.clientY, items);
}

/* ── Project item row menu ── */

function showProjectItemMenu(e: Event, itemId: string): void {
  const me = e as MouseEvent;
  const item = projectStore.getState().getItem(itemId);
  if (!item) return;

  // Ensure item is selected
  if (!selectionStore.getState().selectedProjectItemIds.includes(itemId)) {
    selectionStore.getState().select({ type: 'projectItem', itemId });
  }

  const selectedCount = selectionStore.getState().selectedProjectItemIds.length;

  const items: ContextItem[] = [
    { label: 'Preview', action: () => monitorStore.getState().setPreview(itemId) },
    { label: 'Take to Program', action: () => {
      monitorStore.getState().setPreview(itemId);
      doTake();
    }},
    { label: '', separator: true },
    {
      label: 'New',
      children: [
        { label: 'Graphic', icon: '/assets/icons/graphic.png', action: () => newGraphic() },
        { label: 'After Effects Comp', icon: '/assets/icons/ae.png', action: () => importAfterEffects() },
        { label: 'Shot Layout', icon: '/assets/icons/shot.png', action: () => newShotLayout() },
        { label: 'Media Sequence', icon: '/assets/icons/sequence.png', action: () => importMedia() },
        { label: 'Transition', icon: '/assets/icons/transition.png', action: () => flashStatus('Transition') },
        {
          label: 'Live Audio/Video',
          children: [
            { label: 'Windows Device', action: () => addLiveCamera() },
            { label: 'Blackmagic Decklink', action: () => addLiveCamera() },
            { label: 'Audio Device', disabled: true },
            { label: 'Screen', disabled: true },
          ],
        },
      ],
    },
    {
      label: 'Import',
      children: [
        { label: 'Layer...', action: () => importLayer() },
        { label: 'Graphic...', action: () => importGraphic() },
        { label: 'Media...', action: () => importMedia() },
        { label: 'After Effects...', action: () => importAfterEffects() },
        { label: 'Shot Layout...', action: () => importShotLayout() },
        { label: 'Transitions...', action: () => importTransitions() },
      ],
    },
    {
      label: 'Manage Shot Contents',
      children: [
        { label: 'Remove from shot', disabled: true, children: [] },
        { label: 'Color Label', children: [
          { label: 'None', action: () => flashStatus('Color Label: None') },
          { label: 'Red', action: () => flashStatus('Color Label: Red') },
          { label: 'Orange', action: () => flashStatus('Color Label: Orange') },
          { label: 'Yellow', action: () => flashStatus('Color Label: Yellow') },
          { label: 'Green', action: () => flashStatus('Color Label: Green') },
          { label: 'Blue', action: () => flashStatus('Color Label: Blue') },
          { label: 'Purple', action: () => flashStatus('Color Label: Purple') },
        ]},
        { label: 'Export shot layout...', action: () => flashStatus('Export shot layout...') },
      ],
    },
    { label: '', separator: true },
    { label: 'Create Shot from Item', action: () => {
      shotStore.getState().addShot(item.name, [{ projectItemId: itemId, action: 'show' }]);
    }},
    { label: '', separator: true },
    { label: 'Rename...', action: () => {
      const name = prompt('Item name:', item.name);
      if (name) projectStore.getState().updateItem(itemId, { name });
    }},
    { label: 'Set Hot Key...', action: () => {
      const key = prompt('Hot key:', item.hotKey || '');
      if (key !== null) projectStore.getState().updateItem(itemId, { hotKey: key || undefined });
    }},
    { label: '', separator: true },
    { label: selectedCount > 1 ? `Delete ${selectedCount} items` : 'Delete', action: () => {
      const ids = selectionStore.getState().selectedProjectItemIds;
      projectStore.getState().removeItems(ids);
      selectionStore.getState().clearSelection();
    }},
  ];

  showContextMenu(me.clientX, me.clientY, items);
}

/* ── Shot launcher menu ── */

function showShotMenu(e: Event, shotId: string): void {
  const me = e as MouseEvent;
  const shot = shotStore.getState().shots.find((s) => s.id === shotId);
  if (!shot) return;

  const items: ContextItem[] = [
    { label: 'Trigger Shot', action: () => shotStore.getState().triggerShot(shotId) },
    { label: 'Rename...', action: () => {
      const name = prompt('Shot name:', shot.name);
      if (name) shotStore.getState().updateShot(shotId, { name });
    }},
    { label: '', separator: true },
    { label: 'Delete Shot', action: () => shotStore.getState().removeShot(shotId) },
  ];

  showContextMenu(me.clientX, me.clientY, items);
}

/* ── Generic context menu renderer with submenu support ── */

let activeCloseHandler: (() => void) | null = null;
let openSubmenus: { el: HTMLDivElement; parentMenu: HTMLDivElement }[] = [];

function showContextMenu(x: number, y: number, items: ContextItem[]): void {
  // Remove existing menus
  document.querySelectorAll('.context-menu').forEach((el) => el.remove());
  if (activeCloseHandler) {
    document.removeEventListener('click', activeCloseHandler);
    activeCloseHandler = null;
  }

  const menu = buildMenu(items);
  menu.style.cssText = `position:fixed;left:${x}px;top:${y}px;z-index:2000;`;
  document.body.appendChild(menu);

  // Keep menu on screen
  requestAnimationFrame(() => {
    const rect = menu.getBoundingClientRect();
    if (rect.right > window.innerWidth) menu.style.left = `${window.innerWidth - rect.width - 4}px`;
    if (rect.bottom > window.innerHeight) menu.style.top = `${window.innerHeight - rect.height - 4}px`;
  });

  // Close on outside click
  const close = () => {
    document.querySelectorAll('.context-menu').forEach((el) => el.remove());
    openSubmenus = [];
    document.removeEventListener('click', close);
    activeCloseHandler = null;
  };
  activeCloseHandler = close;
  setTimeout(() => document.addEventListener('click', close), 0);
}

function buildMenu(items: ContextItem[]): HTMLDivElement {
  const menu = document.createElement('div');
  menu.className = 'context-menu';

  for (const item of items) {
    if (item.separator) {
      const sep = document.createElement('div');
      sep.className = 'ctx-separator';
      menu.appendChild(sep);
      continue;
    }

    const el = document.createElement('div');
    el.className = `ctx-item${item.disabled ? ' disabled' : ''}${item.children ? ' has-submenu' : ''}`;

    // Icon
    let iconHtml = '';
    if (item.icon) {
      iconHtml = `<img class="ctx-icon" src="${item.icon}" alt="">`;
    }

    // Submenu arrow
    const arrow = item.children ? '<span class="ctx-arrow">&#9656;</span>' : '';
    el.innerHTML = `${iconHtml}<span class="ctx-label">${item.label}</span>${arrow}`;

    // Submenu hover
    if (item.children) {
      let submenu: HTMLDivElement | null = null;
      let hoverTimeout: ReturnType<typeof setTimeout> | null = null;

      const openSub = () => {
        if (hoverTimeout) { clearTimeout(hoverTimeout); hoverTimeout = null; }
        if (submenu) return; // already open

        // Close sibling submenus
        openSubmenus.forEach((s) => { if (s.parentMenu === menu) { s.el.remove(); } });
        openSubmenus = openSubmenus.filter((s) => s.parentMenu !== menu);

        submenu = buildMenu(item.children!);
        const elRect = el.getBoundingClientRect();
        const menuRect = menu.getBoundingClientRect();

        // Position to the right of the parent item
        let left = menuRect.right;
        let top = elRect.top;

        submenu.style.cssText = `position:fixed;left:${left}px;top:${top}px;z-index:2001;`;
        document.body.appendChild(submenu);
        openSubmenus.push({ el: submenu, parentMenu: menu });

        // Close submenu when mouse leaves it (unless entering parent item)
        submenu.addEventListener('mouseleave', (ev) => {
          const related = (ev as MouseEvent).relatedTarget as HTMLElement | null;
          if (related && (el.contains(related) || el === related)) return;
          closeSub();
        });
        // Cancel close when re-entering submenu
        submenu.addEventListener('mouseenter', () => {
          if (hoverTimeout) { clearTimeout(hoverTimeout); hoverTimeout = null; }
        });

        // Keep on screen
        requestAnimationFrame(() => {
          if (!submenu) return;
          const sr = submenu.getBoundingClientRect();
          if (sr.right > window.innerWidth) {
            submenu.style.left = `${menuRect.left - sr.width}px`;
          }
          if (sr.bottom > window.innerHeight) {
            submenu.style.top = `${window.innerHeight - sr.height - 4}px`;
          }
        });
      };

      const closeSub = () => {
        hoverTimeout = setTimeout(() => {
          if (submenu) {
            openSubmenus = openSubmenus.filter((s) => s.el !== submenu);
            submenu.remove();
            submenu = null;
          }
        }, 200);
      };

      el.addEventListener('mouseenter', openSub);
      el.addEventListener('mouseleave', (e) => {
        const related = e.relatedTarget as HTMLElement | null;
        if (submenu && related && (submenu.contains(related) || submenu === related)) return;
        closeSub();
      });

      // Keep submenu open when hovering over it
      el.addEventListener('mouseenter', () => {
        if (hoverTimeout) { clearTimeout(hoverTimeout); hoverTimeout = null; }
      });
    } else if (!item.disabled && item.action) {
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        item.action!();
        // Close all menus
        document.querySelectorAll('.context-menu').forEach((m) => m.remove());
        openSubmenus = [];
        if (activeCloseHandler) {
          document.removeEventListener('click', activeCloseHandler);
          activeCloseHandler = null;
        }
      });
    }

    menu.appendChild(el);
  }

  return menu;
}
